import { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { DocumentDTO } from 'constants/TypeConstants';
import { Place } from 'features/place/services/createPlace';
import { TAG_PRODUCTS } from 'services/TagsConstants';
import { store } from 'store/store';

import { Country } from '../../../services/country';
import { createQueryString } from '../../../utils/helpers';
import { ProductCategory } from '../../productCategory/services/getProductCategories';
import { ProductDTO } from './getProductById';

const BASE_URL = '/products';

export default (build: EndpointBuilder<BaseQueryFn, string, string>) =>
  build.query<Products, ProductParameters>({
    async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
      const { place } = _arg;
      const productResponse = await fetchWithBQ(createQueryString(_arg, BASE_URL));
      const productsData = productResponse.data as Products;
      const placeResponse = await fetchWithBQ(`/place/${place}`);
      const placeData = placeResponse.data as Place;
      const documents = productsData.content.map((product: Product) => {
        return product?.document ? product?.document : null;
      });
      const documentIds = documents.filter((id) => id !== null);
      const documentResponse =
        documentIds?.length > 0
          ? await fetchWithBQ(`/documents/download-links?ids=${documentIds}`)
          : null;
      const documentData = documentResponse?.data as DocumentDTO;
      const countries = store.getState().globalData.countries;
      const products = productsData.content.map((product: Product) => {
        const { basePrice, discountedPrice, name } = product;
        const matchedCountry = countries.find(
          (country: Country) => country.uuid === placeData?.country,
        );
        const { isoCurrency } = matchedCountry;
        return {
          ...product,
          document: documentData ? documentData[product?.document] : null,
          basePrice: `${basePrice} ${isoCurrency}`,
          discountedPrice: `${discountedPrice} ${isoCurrency}`,
          productName: name,
        };
      });
      return {
        data: {
          content: products,
          totalElements: productsData?.totalElements,
          totalPages: productsData?.totalPages,
          place: placeData,
          currentPage: productsData?.pageable?.pageNumber,
        },
      };
    },
    providesTags: [TAG_PRODUCTS],
  });

export type ProductParameters = {
  categoryId?: string;
  ids?: string;
  place?: string;
};

export type Products = {
  content: Product[];
  currentPage?: number;
  pageable?: {
    pageNumber?: number;
  };
  place?: Place;
  totalElements?: number;
  totalPages?: number;
};

export type Product = Omit<ProductDTO, 'category'> & {
  category: ProductCategory | string;
  createdAt: string;
  createdBy: string;
  oldAvailability?: number;
  orderCount: number;
  rated: boolean;
  ratingCount: number;
  ratingSum: number;
  updatedAt: string;
  updatedBy: string;
  userOrderCount: number;
  version: number;
};
